import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

function detectDeviceType(): "mobile" | "tablet" | "desktop" {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return "mobile";
  return "desktop";
}

/**
 * Tracks user sessions invisibly.
 *
 * On mount:      inserts a row in user_sessions and saves its id
 * Every 60 s:    updates ended_at + duration_seconds (heartbeat)
 * On unmount:    final update so the row is always closed
 * Visibility:    pauses the heartbeat while the tab is hidden; resumes on focus
 */
export function useSessionTracker() {
  const { user, session } = useAuth();
  const sessionRowId = useRef<string | null>(null);
  const startedAt = useRef<Date | null>(null);
  const isVisible = useRef(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── helpers ────────────────────────────────────────────────────
  const updateRow = async () => {
    if (!sessionRowId.current || !startedAt.current) return;
    const endedAt = new Date();
    const duration = Math.round((endedAt.getTime() - startedAt.current.getTime()) / 1000);
    await supabase
      .from("user_sessions")
      .update({ ended_at: endedAt.toISOString(), duration_seconds: duration })
      .eq("id", sessionRowId.current);
  };

  const startHeartbeat = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      if (isVisible.current) updateRow();
    }, 60_000);
  };

  const stopHeartbeat = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    // Only track authenticated users
    if (!user?.id || !session) return;

    let isMounted = true;

    const init = async () => {
      const supabaseUser = (await supabase.auth.getSession()).data.session?.user;
      if (!supabaseUser || !isMounted) return;

      startedAt.current = new Date();
      const { data, error } = await supabase
        .from("user_sessions")
        .insert({
          user_id: supabaseUser.id,
          started_at: startedAt.current.toISOString(),
          device_type: detectDeviceType(),
        })
        .select("id")
        .single();

      if (error || !data) {
        console.warn("[useSessionTracker] insert failed:", error?.message);
        return;
      }

      sessionRowId.current = data.id;
      startHeartbeat();
    };

    init();

    // Pause heartbeat when tab is hidden; resume + do an immediate update on show
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        isVisible.current = false;
        updateRow();
      } else {
        isVisible.current = true;
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      isMounted = false;
      stopHeartbeat();
      document.removeEventListener("visibilitychange", handleVisibility);
      // Final update — fire-and-forget, runs even after unmount
      updateRow();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}
